import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { User } from '@/types';

import { getMfaRedirectTarget } from '@/hooks/mfaRedirect';
import { useGetAuthStatusQuery } from '@/store/api/authApi';
import { useGetCurrentUserQuery } from '@/store/api/userApi';
import { isCoach } from '@/types/user';
import {
  captureException,
  captureMessage,
  setErrorMonitoringUser,
} from '@/utils/errorMonitoring';
import { restoreAnalyticsIdentityForAuthenticatedUser } from '@/utils/tracking';

const MFA_PROMPT_SKIPPED_UNTIL_KEY = 'mfa_prompt_skipped_until';

const getSkippedUntil = (): number => {
  try {
    const raw = window.localStorage.getItem(MFA_PROMPT_SKIPPED_UNTIL_KEY);
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
};

export const useCurrentUser = (setup = true) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const enabled =
    !pathname.includes('signin') &&
    !pathname.includes('reset') &&
    !pathname.includes('authenticate') &&
    !pathname.includes('mfa') &&
    !pathname.includes('verify-email') &&
    setup;

  // Split redirect gating:
  // - Auth redirects (unauth + MFA/email verification) must still run on /get-started, otherwise the page can go blank.
  // - Onboarding redirects (assessment/payment) should not run on /get-started to avoid loops within the onboarding flow.
  const shouldCheckAuthRedirects = enabled;
  const shouldCheckOnboardingRedirects =
    enabled &&
    !pathname.includes('get-started') &&
    !pathname.startsWith('/m/book-chemistry-call') &&
    !pathname.startsWith('/m/book-biweekly') &&
    !pathname.startsWith('/m/book-single-session');

  const { data: authStatus, isLoading: isAuthStatusLoading } =
    useGetAuthStatusQuery(enabled ? undefined : skipToken);

  const isMfaRequired = !!authStatus?.mfaRequired;
  const isMfaPrompt = !!authStatus?.mfaPrompt;
  const isEmailVerified = authStatus?.emailVerified ?? true;

  // Check if user has skipped the MFA prompt (stored in localStorage)
  const skippedUntil = getSkippedUntil();
  const hasSkippedMfaPrompt = isMfaPrompt && skippedUntil > Date.now();
  const needsEmailVerification =
    !isEmailVerified &&
    (isMfaRequired || (isMfaPrompt && !hasSkippedMfaPrompt));

  const {
    currentData,
    data: user = {} as User,
    error,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch: refetchUser,
  } = useGetCurrentUserQuery(
    enabled && !isAuthStatusLoading && !isMfaRequired ? undefined : skipToken,
  );

  const needsAssessment =
    !isCoach(user) &&
    user.id &&
    !user.userAssesment?.complete &&
    shouldCheckOnboardingRedirects;

  const needsPayment =
    !isCoach(user) &&
    pathname.startsWith('/m') &&
    user.id &&
    user.userAssesment?.complete &&
    !user.isPaid &&
    !user.isBusinessPaid &&
    shouldCheckOnboardingRedirects;

  useEffect(() => {
    if (enabled && (isAuthStatusLoading || isLoading)) return;

    setErrorMonitoringUser({ email: user.email, id: user.id });
  }, [enabled, isAuthStatusLoading, isLoading, user.email, user.id]);

  useEffect(() => {
    if (!enabled || !isSuccess || !user.id || !user.email) return;

    restoreAnalyticsIdentityForAuthenticatedUser(user);
  }, [enabled, isSuccess, user]);

  useEffect(() => {
    if (!shouldCheckAuthRedirects) return;

    if (isMfaRequired || (isMfaPrompt && !hasSkippedMfaPrompt)) {
      const redirectTo = `${pathname}${search}`;
      const target = getMfaRedirectTarget({
        emailVerified: isEmailVerified,
        hasSkippedMfaPrompt,
        mfaPrompt: isMfaPrompt,
        mfaRequired: isMfaRequired,
      });
      if (!target) return;
      navigate(
        `${target}${
          redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ''
        }`,
        { replace: true },
      );
      return;
    }

    if (shouldCheckOnboardingRedirects && needsAssessment) {
      const redirectTo = `${pathname}${search}`;
      navigate(
        `/get-started${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`,
        { replace: true },
      );
      return;
    }

    if (shouldCheckOnboardingRedirects && needsPayment) {
      navigate('/get-started/membership-select', { replace: true });
    }
  }, [
    enabled,
    isMfaPrompt,
    isMfaRequired,
    needsEmailVerification,
    needsAssessment,
    needsPayment,
    navigate,
    pathname,
    search,
    shouldCheckAuthRedirects,
    shouldCheckOnboardingRedirects,
  ]);

  useEffect(() => {
    if (error) {
      captureMessage('useCurrentUser received error', 'error');
      captureException(error);
    }
  }, [error]);

  return {
    currentData,
    isError,
    isFetching,
    isSuccess,
    // This way invalid users will show up as "loading" instead of broken
    // Keep loading state true when MFA, assessment, or payment redirect is pending to prevent rendering broken page
    // Don't keep loading for skipped MFA prompts - user chose to skip, let them proceed
    isUserLoading:
      isAuthStatusLoading ||
      isLoading ||
      isMfaRequired ||
      (isMfaPrompt && !hasSkippedMfaPrompt) ||
      needsEmailVerification ||
      needsAssessment ||
      needsPayment ||
      (!user.id && enabled) ||
      !enabled,
    refetchUser,
    user,
    userEnabled: enabled,
  };
};
